import {
  additionalStudentTypes,
  custodyOptions,
  ethnicityOptions,
  genderOptions,
  raceOptions,
  studentsTypes,
  veteranOptions,
  yesNoBooleanOptions,
  disabledOptions,
  DO_NOT_WISH_PROVIDE_OPTION,
} from '../../../components/LeaseAffordableApplicantFormCommonSections/configuration';
import {
  withConditionallyRequiredIcon,
  getAffordableRelationshipRules,
} from './fieldRules';
import messages from './messages';

export const MULTI_RACE_FIELD = 'affordableInformation.multiRace';

// Conventional sees a subset of the same list, not a second list: product
// intends to widen this allowlist later.
export const CONVENTIONAL_DEMOGRAPHIC_FIELDS = [
  'affordableInformation.specialNeedsDesignationIds',
  'personalInformation.annualIncome',
];

// clearableSelectProps is passed, not imported: it lives in
// useMiniProfileSections, and importing it back would be circular.
export const buildDemographicFields = ({
  isAffordable = false,
  isAdult = false,
  isPending = false,
  isPrimary = false,
  isCascadedApplicantType = false,
  clearableSelectProps,
  formatMessage,
  affordableRelationshipOptions = [],
  affordableRelationshipRequired = false,
  wasAffordableRelationshipCleared = false,
  disabilitySubFieldsDisabled = false,
  specialNeedsDesignations = [],
  annualIncomeValidate,
  showMultiRace = false,
  multiRaceOptions = [],
  // Injectable rather than a closed-over import, so a caller (or a test) can
  // extend the allowlist without mutating the shared exported constant.
  conventionalFields = CONVENTIONAL_DEMOGRAPHIC_FIELDS,
}) => {
  const fields = [
    {
      variant: 'singleSelect',
      name: 'affordableInformation.gender',
      label: withConditionallyRequiredIcon('Gender'),
      ...clearableSelectProps(genderOptions),
      placeholder: 'Choose',
      GridProps: { xs: 12, md: 6 },
    },
    {
      variant: 'singleSelect',
      name: isAdult
        ? 'affordableInformation.veteran'
        : 'affordableInformation.jointCustody',
      label: withConditionallyRequiredIcon(
        isAdult ? 'US Military Veteran' : 'Joint Custody',
      ),
      ...clearableSelectProps(isAdult ? veteranOptions : custodyOptions),
      placeholder: 'Choose',
      GridProps: { xs: 12, md: 6 },
    },
    {
      variant: 'singleSelect',
      name: 'affordableInformation.disability.hasDisability',
      label: withConditionallyRequiredIcon('Disabled'),
      // Ungated on purpose: the form stores 'notWishToProvide' without
      // consulting doNotWishToProvideDisability, so gating would blank it here.
      ...clearableSelectProps(disabledOptions),
      placeholder: 'Choose',
      GridProps: { xs: 12, md: 6 },
    },
    {
      variant: 'singleSelect',
      name: 'affordableInformation.disability.disabilityMobile',
      label: withConditionallyRequiredIcon('Mobile Disability'),
      ...clearableSelectProps(yesNoBooleanOptions),
      placeholder: 'Choose',
      disabled: disabilitySubFieldsDisabled,
      GridProps: { xs: 12, md: 6 },
    },
    {
      variant: 'singleSelect',
      name: 'affordableInformation.disability.disabilityHearing',
      label: withConditionallyRequiredIcon('Hearing Disability'),
      ...clearableSelectProps(yesNoBooleanOptions),
      placeholder: 'Choose',
      disabled: disabilitySubFieldsDisabled,
      GridProps: { xs: 12, md: 6 },
    },
    {
      variant: 'singleSelect',
      name: 'affordableInformation.disability.disabilityVisual',
      label: withConditionallyRequiredIcon('Vision Disability'),
      ...clearableSelectProps(yesNoBooleanOptions),
      placeholder: 'Choose',
      disabled: disabilitySubFieldsDisabled,
      GridProps: { xs: 12, md: 6 },
    },
    {
      variant: 'singleSelect',
      name: 'affordableInformation.disability.disabilityOther',
      label: withConditionallyRequiredIcon('Other Disability'),
      ...clearableSelectProps(yesNoBooleanOptions),
      placeholder: 'Choose',
      disabled: disabilitySubFieldsDisabled,
      GridProps: { xs: 12, md: 6 },
    },
    showMultiRace
      ? {
          variant: 'multiSelect',
          name: MULTI_RACE_FIELD,
          label: withConditionallyRequiredIcon('Race'),
          options: multiRaceOptions,
          excludeFromSelectAll: [DO_NOT_WISH_PROVIDE_OPTION.value],
          placeholder: 'Choose',
          GridProps: { xs: 12, md: 6 },
        }
      : {
          variant: 'singleSelect',
          name: 'affordableInformation.race',
          label: withConditionallyRequiredIcon('Race'),
          ...clearableSelectProps(raceOptions()),
          placeholder: 'Choose',
          GridProps: { xs: 12, md: 6 },
        },
    {
      variant: 'singleSelect',
      name: 'affordableInformation.ethnicity',
      label: withConditionallyRequiredIcon('Ethnicity'),
      ...clearableSelectProps(ethnicityOptions),
      placeholder: 'Choose',
      GridProps: { xs: 12, md: 6 },
    },
    {
      variant: 'singleSelect',
      name: 'affordableInformation.affordableRelationshipId',
      // Required, not conditionally required, so it carries no icon.
      label: 'Affordable Relation',
      ...clearableSelectProps(affordableRelationshipOptions),
      placeholder: 'Choose',
      // Never required while read-only (Pending minor): a blank legacy value on
      // a field the user can't touch would block Save for good.
      ...((!isAdult && !isPending) || affordableRelationshipRequired
        ? {
            rules: getAffordableRelationshipRules({ formatMessage }),
            required: true,
          }
        : {}),
      // Auto-set to "Head of Household" elsewhere, and owned by the Type
      // cascade while a cascaded type is selected -- editing it here would
      // let it drift out of sync.
      disabled: isPrimary || isCascadedApplicantType,
      helperText: isPrimary
        ? formatMessage(messages.headOfHouseholdAffordableRelationHelperText)
        : wasAffordableRelationshipCleared
        ? formatMessage(messages.clearedAffordableRelationHelperText)
        : undefined,
      GridProps: { xs: 12, md: 6 },
    },
    ...(!isAdult
      ? [
          {
            variant: 'singleSelect',
            name: 'affordableInformation.studentType',
            label: withConditionallyRequiredIcon('Student'),
            ...clearableSelectProps([
              ...studentsTypes,
              ...additionalStudentTypes,
            ]),
            placeholder: 'Choose',
            GridProps: { xs: 12, md: 6 },
          },
        ]
      : []),
    {
      variant: 'multiSelect',
      name: 'affordableInformation.specialNeedsDesignationIds',
      label: withConditionallyRequiredIcon('Special Needs Designation'),
      options: specialNeedsDesignations,
      placeholder: 'Choose',
      GridProps: { xs: 12, md: 6 },
    },
    // Conventional only, and adults only -- no minor payload persists it.
    ...(!isAffordable && isAdult
      ? [
          {
            variant: 'currency',
            name: 'personalInformation.annualIncome',
            label: withConditionallyRequiredIcon(
              formatMessage(messages.annualIncome),
            ),
            placeholder: '0.00',
            rules: { validate: annualIncomeValidate },
            GridProps: { xs: 12, md: 6 },
          },
        ]
      : []),
  ];

  return isAffordable
    ? fields
    : fields.filter((field) => conventionalFields.includes(field.name));
};

export default buildDemographicFields;
